import {
  BaseWebComponentController,
  IListenerConfig,
  WebComponentEvent,
} from '../../../framework/BaseWebComponentController';
import IntegrationDetailsPanelComponent from '../component/IntegrationDetailsPanelComponent';
import IntegrationDetailsPanelView from '../view/IntegrationDetailsPanelView';
import integrationDetailsPanelTemplate from '../view/template';
import { Integration, Sensor, SensorEnabledStatus, IntegrationEvents } from '../../common/types';
import { IntegrationDetailsPanelEvents } from '../types';
import { ButtonEvents } from '../../../ui/gzButton/types';
import { CreateIntegrationEvent } from '../../addIntegrationPanel/types/CreateIntegrationEvent';
import { GzDialogWebComponent } from '../../../ui/gzDialog';
import { GzButtonWebComponent } from '../../../ui/gzButton';
import { getRequestDefaultOptions } from '../../../utils/request.utils';
import { ToastSeverity } from '../../../ui/gzToastNotification/type';
import { showToast } from '../../../accounts/helpers/showToast';

export default class IntegrationDetailsPanelController extends BaseWebComponentController {
  public view: IntegrationDetailsPanelView;

  constructor(public component: IntegrationDetailsPanelComponent) {
    super(component);

    this.initialSetup({
      viewConstructor: IntegrationDetailsPanelView,
      viewTemplate: integrationDetailsPanelTemplate,
    });
  }

  computeEventListeners(): IListenerConfig[] {
    return [
      {
        element: this.view.dom.$sideMenu,
        event: 'close',
        callback: this.close.bind(this),
      },
      {
        element: this.view.dom.$integrationDetailsButton,
        event: ButtonEvents.CLICK,
        callback: evt => {
          this.component.dispatchEvent(
            new CustomEvent(CreateIntegrationEvent.OnLicenseAdd, {
              detail: {
                companyId: this.component.integration.companyId,
              },
              bubbles: true,
              composed: true,
            })
          );
          this.close();
        },
      },
      {
        element: this.view.dom.$sideMenu,
        event: IntegrationDetailsPanelEvents.SENSOR_SET_ENABLED_STATUS_ACTION,
        callback: (evt: WebComponentEvent) => {
          if (evt.detail.enabledStatus === SensorEnabledStatus.DISABLED) {
            return this.view.openSensorSetDisabledStatusDialog(evt.detail.sensor as Sensor);
          }
          return document.body.dispatchEvent(
            new CustomEvent(IntegrationEvents.SENSOR_SET_ENABLED_STATUS, { detail: evt.detail })
          );
        },
      },
      {
        element: this.view.dom.$sideMenu,
        event: IntegrationDetailsPanelEvents.SET_ENABLED_STATUS_ACTION,
        callback: (evt: WebComponentEvent) => {
          const enabledStatus = evt.detail.enabledStatus;
          const integration = this.component.integration;

          if (enabledStatus === SensorEnabledStatus.DISABLED) {
            return this.view.openSetDisabledStatusDialog(integration);
          }

          return document.body.dispatchEvent(
            new CustomEvent(IntegrationEvents.INTEGRATION_SET_ENABLED_STATUS, {
              detail: {
                integration,
                enabledStatus,
              },
            })
          );
        },
      },
      {
        element: this.view.dom.$sideMenu,
        event: IntegrationDetailsPanelEvents.DELETE_ACTION,
        callback: () => this.view.openDeleteDialog(this.component.integration),
      },
      {
        element: document.body,
        event: IntegrationEvents.SENSOR_SET_ENABLED_STATUS,
        callback: this.close.bind(this),
      },
      {
        element: document.body,
        event: IntegrationEvents.INTEGRATION_SET_ENABLED_STATUS,
        callback: this.close.bind(this),
      },
      {
        element: document.body,
        event: IntegrationEvents.INTEGRATION_DELETE,
        callback: this.close.bind(this),
      },
      {
        element: document.body,
        event: IntegrationEvents.INTEGRATION_EDIT,
        callback: this.close.bind(this),
      },
      {
        element: this.view.dom.$sideMenu,
        event: IntegrationDetailsPanelEvents.EDIT_ACTION,
        callback: () =>
          document.body.dispatchEvent(
            new CustomEvent(IntegrationEvents.INTEGRATION_EDIT, { detail: this.component.integration })
          ),
      },
      {
        element: this.view.dom.$sideMenu,
        event: IntegrationDetailsPanelEvents.BUTTON_CLICKED_DELETE_SENSOR,
        callback: (evt: WebComponentEvent) => this.view.onDeleteSensorConfirmationWindow(evt.detail.sensor),
      },
      {
        element: document.body,
        event: IntegrationEvents.DELETE_SENSOR,
        callback: this.close.bind(this),
      },
    ];
  }

  async getDataForView(): Promise<Integration> {
    return this.component.integration;
  }

  private close(): void {
    this.component.parentNode?.removeChild(this.component);
  }
}
